import { createBrowserRouter, redirect } from 'react-router-dom';
import type { LoaderFunction } from 'react-router-dom';

import SmartHome from '@/components/SmartHome';
import AuthResult from '@/pages/AuthResult';
import AutoRenewalAgreement from '@/pages/AutoRenewalAgreement';
import { ChromeExtensionPage } from '@/pages/ChromeExtension';
import Cli from '@/pages/Cli';
import CliDocs from '@/pages/CliDocs';
import CliOut from '@/pages/CliOut';
import CookiePolicy from '@/pages/CookiePolicy';
import Dashboard from '@/pages/Dashboard';
// import { ProfileTab } from '@/pages/Dashboard/ProfileTab';
import { PlanRelationTab } from '@/pages/Dashboard/PlanRelationTab';
import { UsageTab } from '@/pages/Dashboard/UsageTab';
import Download from '@/pages/Download/index';
import Feedback from '@/pages/Feedback';
import { OAuthPage } from '@/pages/OAuth';
import Pricing from '@/pages/Pricing';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import SubscriptionAgreement from '@/pages/SubscriptionAgreement';
import TermOfUse from '@/pages/TermOfUse';
import WaitList from '@/pages/WaitList';
import ForgotPasswordPage from '@/pages/forgot-password';
import Login from '@/pages/login';
import { Main } from '@/pages/main';
import RegisterPage from '@/pages/register';
import { ErrorPage } from '@/shared/components/ErrorPage';
import { isInternalDomain } from '@/utils/const';

/**
 * CLI 页面包装组件
 * 根据域名类型渲染不同的 CLI 组件
 * - 对内域名：渲染 Cli 组件
 * - 对外域名：渲染 CliOut 组件
 */
const CliWrapper = () => {
  return isInternalDomain() ? <Cli /> : <CliOut />;
};

/**
 * 应用路由配置
 * 使用React Router v6的createBrowserRouter创建路由
 */
// 获取 basename，支持环境变量配置，默认为 cn 开头
const getBasename = () => {
  return import.meta.env.VITE_BASE_NAME || '/';
};

// 根据域名类型构建路由配置
const buildRoutes = () => {
  const isInternal = isInternalDomain();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const routes: any[] = [
    {
      path: '/',
      element: <Main />,
      children: [
        {
          index: true,
          element: <SmartHome />,
        },

        {
          path: '/policies/privacy-policy',
          element: <PrivacyPolicy />,
        },
        {
          path: '/policies/cookie-policy',
          element: <CookiePolicy />,
        },
        {
          path: '/policies/terms-of-use',
          element: <TermOfUse />,
        },
        {
          path: '/policies/subscription-agreement',
          element: <SubscriptionAgreement />,
        },
        {
          path: '/policies/auto-renewal-agreement',
          element: <AutoRenewalAgreement />,
        },
      ],
    },
    // {
    //   path: '/download/dev',
    //   element: <DownloadDev />,
    // },
    {
      path: '/download',
      element: <Download />,
    },
    {
      path: '/cli',
      element: <CliWrapper />,
    },
    {
      path: '/cli-docs',
      element: <CliDocs />,
    },
    {
      path: '/cli-docs/*',
      element: <CliDocs />,
    },
    {
      path: '/waitlist',
      element: <WaitList />,
    },
    {
      path: '/oauth',
      element: <OAuthPage></OAuthPage>,
    },
    {
      path: '/auth_result',
      element: <AuthResult />,
      errorElement: <ErrorPage />,
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/login-fail',
      element: <Login />,
    },
    {
      path: '/register',
      element: <RegisterPage />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPasswordPage />,
    },
    {
      path: '/chrome-extension',
      element: <ChromeExtensionPage />,
    },
    {
      path: '/feedback',
      element: <Feedback />,
    },
    {
      path: '/dashboard',
      element: <Dashboard />,
      children: [
        {
          index: true,
          loader: () => redirect('/dashboard/usage'),
        },
        {
          path: 'usage',
          element: <UsageTab />,
        },
        {
          path: 'plan',
          element: <PlanRelationTab />,
        },
        // {
        //   path: 'profile',
        //   element: <ProfileTab />,
        // },
      ],
    },
    // 重定向所有未匹配的路由到登录页
    {
      path: '*',
      loader: () => redirect('/'),
    },
  ];

  // 只在对外域名时添加 pricing 路由，并通过 loader 校验白名单
  if (!isInternal) {
    routes.splice(2, 0, {
      path: '/pricing',
      element: <Pricing />,
    });
  }

  return routes;
};

export const router = createBrowserRouter(buildRoutes(), {
  basename: getBasename(),
});
